import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { heroService } from '../services/heroService'; 

gsap.registerPlugin(ScrollTrigger);

const DEFAULT_HERO_SLIDES = [
  {
    id: 1,
    image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=80",
    title: "Discover Your Dream Property",
    subtitle: "Your trusted partner in real estate, helping you find the perfect place to call home in India."
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=80",
    title: "Luxury Living Redefined",
    subtitle: "Experience the finest properties with world-class amenities and stunning locations."
  },
  {
    id: 3,
    image: "https://images.unsplash.com/photo-1600607687939-ce8a09cf159d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=80",
    title: "Premium Properties",
    subtitle: "Explore our curated collection of high-end properties across India's most sought-after locations."
  }
];

const Hero = () => {
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true); 
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroRef = useRef(null);
  const textRef = useRef(null);
  const imageRef = useRef(null); 
  const slideInterval = useRef(null);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        setLoading(true);
        const slides = await heroService.getHeroSlides();
        setHeroSlides(slides);
      } catch (error) {
        console.error("Failed to fetch hero slides:", error);
        setHeroSlides(DEFAULT_HERO_SLIDES);
      } finally {
        setLoading(false);
      }
    };
    fetchSlides();
  }, []); 

  const nextSlide = () => {
    if (heroSlides.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }
  };

  const prevSlide = () => {
    if (heroSlides.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    }
  };
  
  useEffect(() => {
    if (!loading && heroSlides.length > 0) {
      const ctx = gsap.context(() => {
        gsap.to(imageRef.current, {
          yPercent: 20,
          ease: "power1.out",
          willChange: "transform",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 0.5,
            anticipatePin: 1,
            markers: false
          }
        });
      }, heroRef);

      return () => ctx.revert();
    }
  }, [loading, heroSlides.length]); 

  useEffect(() => {
    if (!loading && heroSlides.length > 0) {
      const textChildren = textRef.current?.children;
      if (textChildren) {
        gsap.from(textChildren, {
          opacity: 0,
          y: 50,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          willChange: "opacity, transform"
        });
      }

      if (slideInterval.current) {
        clearInterval(slideInterval.current);
      }
      slideInterval.current = setInterval(nextSlide, 5000);
      
      return () => {
        if (slideInterval.current) {
          clearInterval(slideInterval.current);
        }
      };
    }
  }, [currentSlide, loading, heroSlides.length]); 

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  if (heroSlides.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-xl text-gray-700">No hero slides available. Please check your configuration.</p>
      </div>
    );
  }

  const currentSlideData = heroSlides[currentSlide];

  return (
    <section ref={heroRef} className="relative w-full h-screen overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            ref={index === currentSlide ? imageRef : null}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt="Luxury Property"
              className="absolute inset-0 w-full h-full object-cover"
              loading={index === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
          </div>
        ))}
      </div>

      {/* Hero Content */}
      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-6 sm:px-12 lg:px-16">
          <div key={currentSlideData.id} ref={textRef} className="max-w-4xl ml-8 sm:ml-12 lg:ml-16 space-y-6">
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-display font-bold text-white leading-[1.1] tracking-tight">
              {currentSlideData.title.split(' ').map((word, i) => (
                <span key={i} className={i === 2 ? 'text-accent block' : ''}>
                  {word}{' '}
                </span>
              ))}
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 max-w-2xl font-medium leading-relaxed">
              {currentSlideData.subtitle}
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/properties"
                className="px-6 py-3 text-sm sm:text-base font-semibold text-white bg-accent hover:bg-accent/90 transition-all duration-300 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1"
              >
                Browse Properties
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 text-sm sm:text-base font-semibold text-white border-2 border-white hover:bg-white/10 transition-all duration-300 rounded-full hover:shadow-lg hover:-translate-y-1"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-20 pointer-events-none">
        <div className="w-full md:px-4 flex justify-between">
          <button
            onClick={prevSlide}
            className="pointer-events-auto p-3 md:p-5 rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/40 transition-all duration-300 group ml-4 sm:ml-8"
            aria-label="Previous slide"
          >
            <svg
              className="w-2 h-2 md:w-6 md:h-6 text-white transform group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="pointer-events-auto p-3 md:p-5 rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/40 transition-all duration-300 group mr-4 sm:mr-8"
            aria-label="Next slide"
          >
            <svg
              className="w-2 h-2 md:w-6 md:h-6 text-white transform group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-12 inset-x-0 z-20">
        <div className="flex justify-center space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2.5 transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? 'w-10 bg-accent'
                  : 'w-2.5 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;